/** The shared, versioned sandbox evidence consumed by the IDE and proof. */
export const TEST_RECORD_VERSION = 'mol-test-run/v2' as const

export interface BuildSnapshot {
  buildKey: string
  deployId: string
  digest: string
  fingerprint: string
  activatedAt: string
  /** Last allocated test order when the canonical build became active. */
  activationOrder?: string
}
export type Measurement =
  | { state: 'bound'; snapshot: BuildSnapshot }
  | { state: 'unbound' | 'unknown'; snapshot: null; reason: string }
export interface CaseIdentity {
  suiteId: string
  suiteRevision: string
  file: string
  workspaceFile: string
  titlePath: string[]
  project: string
  definitionRevision: string
  repeatEachIndex?: number
  nativeOccurrence?: number
}
export type CaseOutcome =
  'pass' | 'fail' | 'skip' | 'didNotRun' | 'timedOut' | 'interrupted' | 'unknown'
export interface Applicability {
  state: 'applicable' | 'notApplicable' | 'unknown'
  gateId?: string
  mode?: string
  gateRevision?: string
  annotation?: string
}
export interface Owner {
  bootId: string
  pid: number
  pgid: number
  processStartTicks: string
}
export interface Coverage {
  state: 'complete' | 'incomplete' | 'unknown'
  selectedCaseKeys: string[] | null
  missingCaseKeys: string[] | null
}
export interface SuiteFacts {
  suiteId: string
  workspaceRoot: string
  resolvedConfig: string | null
  mode: string
  settings: string
  suiteRevision: string
}
export interface StartFacts {
  recordKind: 'invocation' | 'cycle'
  order: string
  groupId: string | null
  launcherSessionId: string
  cycleOrdinal: number
  startedAt: string
  source: 'tests' | 'run_tests' | 'executor' | 'platform' | 'autorun'
  origin: { kind: string; id: string | null }
  invocation: { argv: string[]; cwd: string }
  runner: string
  evidenceMode: 'attributed' | 'process'
  recording: 'pending' | 'attached'
  suite: SuiteFacts | null
  target: { kind: 'deployed' | 'local' | 'mixed' | 'unknown'; url: string | null }
  selection: {
    kind: 'whole' | 'cases' | 'files' | 'unknown'
    inventoryRevision: string | null
    expectedCaseKeys: string[] | null
  }
  measurement: Measurement
  owner: Owner
  containment: { state: 'verified' | 'unavailable'; id: string | null; controls: string[] }
  plan?: { id: string; stepId: string; generation: number }
  nativeReport?: { mode: 'private-invocation'; reportId: string } | { mode: 'none'; reason: string }
}
export interface InventoryEntry {
  identity: CaseIdentity
  state: 'selected' | 'disabled' | 'excluded' | 'uncollectable'
  reason?: string
}
export interface TestRunTimeout {
  scope: 'run' | 'command'
  ms: number
}
/** The durable reason for a plan's accepted budget stop. */
export function testRunTimeoutError(timeout: TestRunTimeout): string {
  const label =
    timeout.ms >= 60_000
      ? `${Math.round(timeout.ms / 60_000)}-minute`
      : `${Math.max(1, Math.round(timeout.ms / 1000))}s`
  return timeout.scope === 'command'
    ? `A command passed its ${label} limit and the run was stopped.`
    : `The run passed its ${label} budget and was stopped.`
}
export interface FinishFacts {
  timeout?: TestRunTimeout
  outcome:
    | 'passed'
    | 'failed'
    | 'cancelled'
    | 'interrupted'
    | 'skipped'
    | 'output-limit'
    | 'reporter-error'
    | 'unknown'
  runnerExit: { state: 'known' | 'unknown'; code?: number; signal?: string }
  totals: { passed: number; failed: number; skipped: number }
  durationMs: number
  cause:
    'finished' | 'cancelled' | 'interrupted' | 'cut' | 'output-limit' | 'reporter-error' | 'unknown'
  coverage: Coverage
  cleanup: 'drained' | 'notRequired' | 'unknown'
  recording?: 'attached' | 'not-recorded'
  suiteError?: 'none' | 'unresolved'
  error?: string
}
interface Envelope {
  v: typeof TEST_RECORD_VERSION
  runId: string
  commandId: string
  seq: number
}
export type TestRecordEvent = Envelope &
  (
    | ({ type: 'start' } & StartFacts)
    | { type: 'inventory'; inventoryRevision: string; entries: InventoryEntry[] }
    | {
        type: 'case'
        identity: CaseIdentity
        phase: 'attempt' | 'final'
        attempt?: { ordinal: number; nativeAttemptId: string; outcome: CaseOutcome }
        finalOutcome?: CaseOutcome
        outcomeOrigin: 'native' | 'selection'
        applicability: Applicability
        durationMs?: number
        failureOutput?: string
      }
    | {
        type: 'result'
        reportId: string
        identity: CaseIdentity
        success: boolean
        durationMs: number
      }
    | {
        type: 'runError'
        errorId: string
        error: string
        phase?: string
        affectedCycles?: string[]
      }
    | { type: 'output'; stream: 'stdout' | 'stderr'; chunk: string }
    | { type: 'control'; requestId: string; action: 'stop' | 'skip'; state: string }
    | ({ type: 'finish' } & FinishFacts)
  )
export type StartEvent = Extract<TestRecordEvent, { type: 'start' }>
export type CaseEvent = Extract<TestRecordEvent, { type: 'case' }>
export type FinishEvent = Extract<TestRecordEvent, { type: 'finish' }>
export interface NormalizedTestRecord {
  start: StartEvent
  events: TestRecordEvent[]
  finish: FinishEvent | null
  state: 'live' | 'settled' | 'unavailable'
  rawByteOffset: number
  diagnostics: string[]
}
export interface RecordWatermark {
  generation: string
  records: {
    order: string
    runId: string
    seq: number
    rawByteOffset: number
    terminalProjection: string
  }[]
}
export interface TestRecordSnapshot {
  v: typeof TEST_RECORD_VERSION
  records: NormalizedTestRecord[]
  watermark: RecordWatermark
}

/** Explicit field order is part of the contract; object property order never defines identity. */
export function caseKey(identity: CaseIdentity): string {
  return JSON.stringify([
    identity.suiteId,
    identity.suiteRevision,
    identity.file,
    identity.workspaceFile,
    identity.titlePath,
    identity.project,
    identity.definitionRevision,
    identity.repeatEachIndex ?? null,
    identity.nativeOccurrence ?? null,
  ])
}
export function repetitionGroupKey(identity: CaseIdentity): string {
  const { repeatEachIndex: _repeat, nativeOccurrence: _occurrence, ...definition } = identity
  return caseKey(definition)
}
export function compareOrder(a: string, b: string): number {
  return BigInt(a) < BigInt(b) ? -1 : BigInt(a) > BigInt(b) ? 1 : 0
}
export function validIdentity(value: unknown): value is CaseIdentity {
  if (!value || typeof value !== 'object') return false
  const i = value as Partial<CaseIdentity>
  return (
    ['suiteId', 'suiteRevision', 'file', 'workspaceFile', 'project', 'definitionRevision'].every(
      (k) => typeof i[k as keyof CaseIdentity] === 'string',
    ) &&
    typeof i.file === 'string' &&
    i.file.startsWith('/') &&
    Array.isArray(i.titlePath) &&
    i.titlePath.every((t) => typeof t === 'string') &&
    (i.repeatEachIndex === undefined ||
      (Number.isSafeInteger(i.repeatEachIndex) && i.repeatEachIndex >= 0)) &&
    (i.nativeOccurrence === undefined ||
      (Number.isSafeInteger(i.nativeOccurrence) && i.nativeOccurrence >= 0))
  )
}
const strings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')
const oneOf = (value: unknown, values: readonly string[]): boolean =>
  typeof value === 'string' && values.includes(value)
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
function validStart(value: unknown): value is StartEvent {
  if (
    !object(value) ||
    !oneOf(value.recordKind, ['invocation', 'cycle']) ||
    typeof value.order !== 'string' ||
    !/^\d+$/.test(value.order) ||
    typeof value.startedAt !== 'string' ||
    !Number.isFinite(Date.parse(value.startedAt)) ||
    typeof value.launcherSessionId !== 'string' ||
    !Number.isSafeInteger(value.cycleOrdinal) ||
    !object(value.owner) ||
    !object(value.invocation) ||
    !object(value.measurement) ||
    !object(value.target) ||
    !object(value.selection) ||
    !object(value.containment) ||
    !object(value.origin)
  )
    return false
  const o = value.owner,
    m = value.measurement
  if (
    typeof o.bootId !== 'string' ||
    !Number.isSafeInteger(o.pid) ||
    Number(o.pid) <= 0 ||
    !Number.isSafeInteger(o.pgid) ||
    Number(o.pgid) <= 0 ||
    typeof o.processStartTicks !== 'string' ||
    !/^\d+$/.test(o.processStartTicks) ||
    !strings(value.invocation.argv) ||
    typeof value.invocation.cwd !== 'string' ||
    !value.invocation.cwd.startsWith('/') ||
    typeof value.runner !== 'string' ||
    !oneOf(value.source, ['tests', 'run_tests', 'executor', 'platform', 'autorun'])
  )
    return false
  if (m.state === 'bound') {
    const snapshot = m.snapshot
    if (
      !object(snapshot) ||
      !['buildKey', 'deployId', 'digest', 'fingerprint', 'activatedAt'].every(
        (k) => typeof snapshot[k] === 'string' && snapshot[k] !== '',
      )
    )
      return false
    if (!Number.isFinite(Date.parse(String(snapshot.activatedAt)))) return false
    if (
      snapshot.activationOrder !== undefined &&
      (typeof snapshot.activationOrder !== 'string' || !/^\d+$/.test(snapshot.activationOrder))
    )
      return false
  } else if (
    !oneOf(m.state, ['unbound', 'unknown']) ||
    m.snapshot !== null ||
    typeof m.reason !== 'string'
  )
    return false
  if (
    !oneOf(value.target.kind, ['deployed', 'local', 'mixed', 'unknown']) ||
    !(value.target.url === null || typeof value.target.url === 'string') ||
    !oneOf(value.selection.kind, ['whole', 'cases', 'files', 'unknown']) ||
    !(value.selection.expectedCaseKeys === null || strings(value.selection.expectedCaseKeys))
  )
    return false
  const suite = value.suite
  if (
    suite !== null &&
    (!object(suite) ||
      !['suiteId', 'workspaceRoot', 'mode', 'settings', 'suiteRevision'].every(
        (k) => typeof suite[k] === 'string',
      ) ||
      !(suite.resolvedConfig === null || typeof suite.resolvedConfig === 'string'))
  )
    return false
  return value.recordKind === 'invocation'
    ? value.evidenceMode === 'process' && value.recording === 'pending' && value.cycleOrdinal === 0
    : value.evidenceMode === 'attributed' &&
        value.recording === 'attached' &&
        Number(value.cycleOrdinal) > 0 &&
        suite !== null
}
function validEvent(value: TestRecordEvent, start: StartEvent): boolean {
  const v = value as unknown as Record<string, unknown>
  if (value.type === 'start') return validStart(value)
  if (value.type === 'output')
    return (
      oneOf(value.stream, ['stdout', 'stderr']) &&
      typeof value.chunk === 'string' &&
      Buffer.byteLength(value.chunk) <= 64000
    )
  if (value.type === 'runError')
    return (
      typeof value.errorId === 'string' &&
      typeof value.error === 'string' &&
      (value.affectedCycles === undefined || strings(value.affectedCycles))
    )
  if (value.type === 'control')
    return (
      typeof value.requestId === 'string' &&
      oneOf(value.action, ['stop', 'skip']) &&
      typeof value.state === 'string'
    )
  if (value.type === 'case') {
    if (
      start.recordKind !== 'cycle' ||
      !validIdentity(value.identity) ||
      !object(value.applicability) ||
      !oneOf(value.applicability.state, ['applicable', 'notApplicable', 'unknown']) ||
      !oneOf(value.outcomeOrigin, ['native', 'selection'])
    )
      return false
    if (
      start.suite &&
      (value.identity.suiteId !== start.suite.suiteId ||
        value.identity.suiteRevision !== start.suite.suiteRevision)
    )
      return false
    const outcomes = ['pass', 'fail', 'skip', 'didNotRun', 'timedOut', 'interrupted', 'unknown']
    return value.phase === 'final'
      ? oneOf(value.finalOutcome, outcomes) && value.attempt === undefined
      : value.phase === 'attempt' &&
          object(value.attempt) &&
          Number.isSafeInteger(value.attempt.ordinal) &&
          Number(value.attempt.ordinal) >= 0 &&
          typeof value.attempt.nativeAttemptId === 'string' &&
          oneOf(value.attempt.outcome, outcomes) &&
          value.finalOutcome === undefined
  }
  if (value.type === 'inventory')
    return (
      start.recordKind === 'cycle' &&
      typeof value.inventoryRevision === 'string' &&
      Array.isArray(value.entries) &&
      value.entries.every(
        (e) =>
          object(e) &&
          validIdentity(e.identity) &&
          oneOf(e.state, ['selected', 'disabled', 'excluded', 'uncollectable']),
      )
    )
  if (value.type === 'result')
    return (
      start.nativeReport?.mode === 'private-invocation' &&
      value.reportId === start.nativeReport.reportId &&
      validIdentity(value.identity) &&
      typeof value.success === 'boolean' &&
      Number.isFinite(value.durationMs) &&
      value.durationMs >= 0
    )
  if (
    value.type !== 'finish' ||
    !oneOf(value.outcome, [
      'passed',
      'failed',
      'cancelled',
      'interrupted',
      'skipped',
      'output-limit',
      'reporter-error',
      'unknown',
    ]) ||
    !object(value.runnerExit) ||
    !object(value.coverage) ||
    !object(value.totals) ||
    !Number.isFinite(value.durationMs) ||
    value.durationMs < 0 ||
    !oneOf(value.cause, [
      'finished',
      'cancelled',
      'interrupted',
      'cut',
      'output-limit',
      'reporter-error',
      'unknown',
    ]) ||
    !oneOf(value.cleanup, ['drained', 'notRequired', 'unknown'])
  )
    return false
  if (
    !['passed', 'failed', 'skipped'].every(
      (k) =>
        Number.isSafeInteger(value.totals[k as keyof typeof value.totals]) &&
        Number(value.totals[k as keyof typeof value.totals]) >= 0,
    )
  )
    return false
  if (
    value.timeout !== undefined &&
    (!object(value.timeout) ||
      !oneOf(value.timeout.scope, ['run', 'command']) ||
      !Number.isFinite(value.timeout.ms) ||
      value.timeout.ms <= 0 ||
      value.timeout.ms > 600_000 ||
      value.outcome !== 'cancelled' ||
      value.cause !== 'cut')
  )
    return false
  const exit = value.runnerExit,
    coverage = value.coverage
  if (exit.state === 'known') {
    if (!(
      (Number.isInteger(exit.code) && Number(exit.code) >= 0) ||
      (typeof exit.signal === 'string' && exit.signal.length > 0)
    ))
      return false
  } else if (exit.state !== 'unknown' || exit.code !== undefined || exit.signal !== undefined)
    return false
  if (
    !oneOf(coverage.state, ['complete', 'incomplete', 'unknown']) ||
    !(coverage.selectedCaseKeys === null || strings(coverage.selectedCaseKeys)) ||
    !(coverage.missingCaseKeys === null || strings(coverage.missingCaseKeys))
  )
    return false
  if (
    coverage.state !== 'unknown' &&
    (!strings(coverage.selectedCaseKeys) || !strings(coverage.missingCaseKeys))
  )
    return false
  return start.recordKind === 'cycle'
    ? oneOf(value.suiteError, ['none', 'unresolved'])
    : oneOf(v.recording, ['attached', 'not-recorded'])
}
/** Read only newline-committed UTF-8 bytes; malformed lines cannot become evidence. */
export function parseTestRecord(bytes: Uint8Array): NormalizedTestRecord | null {
  const buffer = Buffer.from(bytes)
  const boundary = buffer.lastIndexOf(10) + 1
  const events: TestRecordEvent[] = []
  const diagnostics: string[] = []
  let start: StartEvent | null = null
  let finish: FinishEvent | null = null
  let seq = 0
  for (const line of buffer.subarray(0, boundary).toString('utf8').split('\n').slice(0, -1)) {
    let value: TestRecordEvent
    try {
      value = JSON.parse(line) as TestRecordEvent
    } catch (_error) {
      diagnostics.push('malformed-line')
      continue
    }
    if (
      !value ||
      value.v !== TEST_RECORD_VERSION ||
      !Number.isSafeInteger(value.seq) ||
      value.seq <= seq ||
      finish
    ) {
      diagnostics.push('invalid-envelope')
      continue
    }
    if (!start) {
      if (
        value.type !== 'start' ||
        value.seq !== 1 ||
        !validStart(value) ||
        !value.runId ||
        !value.commandId
      ) {
        diagnostics.push('missing-start')
        continue
      }
      start = value
    } else if (
      value.runId !== start.runId ||
      value.commandId !== start.commandId ||
      value.type === 'start'
    ) {
      diagnostics.push('identity-mismatch')
      continue
    }
    if (!validEvent(value, start)) {
      diagnostics.push('invalid-event')
      continue
    }
    seq = value.seq
    events.push(value)
    if (value.type === 'finish') finish = value
  }
  return start
    ? {
        start,
        events,
        finish,
        state: finish ? 'settled' : 'unavailable',
        rawByteOffset: boundary,
        diagnostics,
      }
    : null
}
export function recordSnapshot(
  records: NormalizedTestRecord[],
  generation = '0',
): TestRecordSnapshot {
  records.sort(
    (a, b) =>
      compareOrder(a.start.order, b.start.order) || a.start.runId.localeCompare(b.start.runId),
  )
  return {
    v: TEST_RECORD_VERSION,
    records,
    watermark: {
      generation,
      records: records.map((r) => ({
        order: r.start.order,
        runId: r.start.runId,
        seq: r.events.at(-1)?.seq ?? 0,
        rawByteOffset: r.rawByteOffset,
        terminalProjection: r.finish
          ? `settled:${r.finish.seq}:${r.finish.outcome}:${r.finish.cleanup}`
          : r.state,
      })),
    },
  }
}
export function sameWatermark(a: RecordWatermark, b: RecordWatermark): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}
export function finalCases(record: NormalizedTestRecord): Map<string, CaseEvent> {
  const finals = new Map<string, CaseEvent>()
  const conflicts = new Set<string>()
  for (const e of record.events) {
    if (e.type !== 'case' || e.phase !== 'final') continue
    const key = caseKey(e.identity)
    const prior = finals.get(key)
    if (
      prior &&
      (prior.finalOutcome !== e.finalOutcome ||
        JSON.stringify(prior.applicability) !== JSON.stringify(e.applicability))
    )
      conflicts.add(key)
    else finals.set(key, e)
  }
  for (const key of conflicts) finals.delete(key)
  return finals
}
